// src/codebase/pages/ScoringRulesPage.jsx
//
// Per-engagement health-score weighting. Route: /scoringrules.

import { useEffect, useMemo, useState } from "react";
import { Save, RefreshCw, XCircle, SlidersHorizontal } from "lucide-react";
import { PageHeader, Button, SurfaceCard, FormField, Input, Alert, EmptyState } from "../../shared/ui";
import { scoringApi } from "../../api/scoringApi";
import { getApiError } from "../../api/client";
import { useWorkspace } from "../../context/WorkspaceContext";

export default function ScoringRulesPage() {
  const { currentEngagementId } = useWorkspace();

  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [baseScore, setBaseScore] = useState(100);
  const [minScore, setMinScore] = useState(0);
  const [maxScore, setMaxScore] = useState(100);

  const [wCritical, setWCritical] = useState(15);
  const [wHigh, setWHigh] = useState(8);
  const [wMedium, setWMedium] = useState(3);
  const [wLow, setWLow] = useState(1);

  const payload = useMemo(
    () => ({
      base_score: Number(baseScore),
      min_score: Number(minScore),
      max_score: Number(maxScore),
      weights_json: {
        critical: Number(wCritical),
        high: Number(wHigh),
        medium: Number(wMedium),
        low: Number(wLow),
      },
    }),
    [baseScore, minScore, maxScore, wCritical, wHigh, wMedium, wLow]
  );

  const load = async () => {
    if (!currentEngagementId) {
      setLoading(false);
      return;
    }
    setErr("");
    setInfo("");
    setLoading(true);
    try {
      const res = await scoringApi.get(currentEngagementId);
      const cfg = res.data || {};
      setBaseScore(cfg.base_score ?? 100);
      setMinScore(cfg.min_score ?? 0);
      setMaxScore(cfg.max_score ?? 100);

      const w = cfg.weights_json || {};
      setWCritical(w.critical ?? 15);
      setWHigh(w.high ?? 8);
      setWMedium(w.medium ?? 3);
      setWLow(w.low ?? 1);
    } catch (e) {
      setErr(getApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!currentEngagementId) return;
    setErr("");
    setInfo("");
    setSaving(true);
    try {
      await scoringApi.update(currentEngagementId, payload);
      setInfo("Scoring rules updated.");
    } catch (e) {
      setErr(getApiError(e));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEngagementId]);

  return (
    <div>
      <PageHeader
        title="Scoring Rules"
        subtitle="Configure this engagement's repo health score weights (Critical/High/Medium/Low)."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={load} loading={loading} disabled={!currentEngagementId}>
              <RefreshCw size={15} /> Refresh
            </Button>
            <Button
              variant="primary"
              onClick={save}
              loading={saving}
              disabled={loading || !currentEngagementId}
            >
              <Save size={15} /> Save
            </Button>
          </div>
        }
      />

      {!currentEngagementId ? (
        <EmptyState
          icon={SlidersHorizontal}
          title="No engagement selected"
          description="Select an engagement from the switcher to configure its scoring rules."
        />
      ) : (
        <>
          {err && (
            <Alert variant="danger" icon={XCircle} title="Something went wrong" className="mb-4">
              {err}
            </Alert>
          )}
          {info && (
            <Alert variant="success" title="Saved" className="mb-4">
              {info}
            </Alert>
          )}

          <SurfaceCard title="Health score formula" className="mb-4">
            <p className="text-sm text-muted mb-4">
              score = base_score − (critical × wCritical) − (high × wHigh) − (medium × wMedium) − (low × wLow),
              then clamped to min_score..max_score
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Base Score">
                <Input type="number" value={baseScore} onChange={(e) => setBaseScore(e.target.value)} />
              </FormField>
              <FormField label="Min Score">
                <Input type="number" value={minScore} onChange={(e) => setMinScore(e.target.value)} />
              </FormField>
              <FormField label="Max Score" helperText="Keep min <= max">
                <Input type="number" value={maxScore} onChange={(e) => setMaxScore(e.target.value)} />
              </FormField>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <FormField label="Critical Weight">
                <Input type="number" value={wCritical} onChange={(e) => setWCritical(e.target.value)} />
              </FormField>
              <FormField label="High Weight">
                <Input type="number" value={wHigh} onChange={(e) => setWHigh(e.target.value)} />
              </FormField>
              <FormField label="Medium Weight">
                <Input type="number" value={wMedium} onChange={(e) => setWMedium(e.target.value)} />
              </FormField>
              <FormField label="Low Weight">
                <Input type="number" value={wLow} onChange={(e) => setWLow(e.target.value)} />
              </FormField>
            </div>
          </SurfaceCard>

          <SurfaceCard title="Current payload preview">
            <pre className="text-xs font-mono whitespace-pre-wrap m-0">{JSON.stringify(payload, null, 2)}</pre>
          </SurfaceCard>
        </>
      )}
    </div>
  );
}
